import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';

const fmt = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);

/**
 * Una cifra del panel. Con `ir` se vuelve un enlace a la pantalla donde están esos
 * clientes: ver que hay 3 sin conexión anotada no sirve de nada si después hay que
 * adivinar cómo encontrarlos.
 */
const Card = ({ icon, valor, label, color='#00E5FF', sub, ir }) => {
  const cuerpo = (
    <>
      <div style={{ fontSize:'22px' }}>{icon}</div>
      <div style={{ fontSize:'26px', fontWeight:'700', color, lineHeight:1 }}>{valor}</div>
      <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.5)' }}>{label}</div>
      {sub && <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)', marginTop:'2px' }}>{sub}</div>}
      {ir && <div style={{ fontSize:'11px', color, marginTop:'4px' }}>Ver cuáles →</div>}
    </>
  );
  const estilo = { background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)',
                   borderRadius:'16px', padding:'18px 20px', display:'flex',
                   flexDirection:'column', gap:'6px' };
  return ir
    ? <Link to={ir} style={{ ...estilo, textDecoration:'none', cursor:'pointer' }}>{cuerpo}</Link>
    : <div style={estilo}>{cuerpo}</div>;
};

const SecTitle = ({ children }) => (
  <p style={{ fontSize:'12px', fontWeight:'700', color:'rgba(255,255,255,0.35)', letterSpacing:'1.2px', textTransform:'uppercase', marginBottom:'12px', marginTop:'4px' }}>{children}</p>
);

export default function Dashboard() {
  const [clientes, setClientes] = useState({});
  const [cartera, setCartera]   = useState({});

  useEffect(() => {
    api.get('/api/clientes/stats/resumen').then(r => setClientes(r.data)).catch(() => {});
    api.get('/api/cartera/resumen-dashboard').then(r => setCartera(r.data)).catch(() => {});
  }, []);

  const fecha = new Date().toLocaleDateString('es-CO', { weekday:'long', year:'numeric', month:'long', day:'numeric' });
  const mesActual = new Date().toLocaleDateString('es-CO', { month:'long', year:'numeric' });

  const revendedores = clientes.por_revendedor || [];
  const proveedores  = clientes.por_proveedor  || [];
  const infra        = clientes.infraestructura || {};

  return (
    <Layout>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
        <h1 style={{ fontSize:'24px', fontWeight:'700' }}>Dashboard</h1>
        <span style={{ fontSize:'13px', color:'rgba(255,255,255,0.4)', textTransform:'capitalize' }}>{fecha}</span>
      </div>

      <SecTitle>Clientes</SecTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'12px', marginBottom:'28px' }}>
        <Card icon="👥" valor={clientes.activos||0}     label="Activos"          color="#00E5FF"/>
        <Card icon="⚠️"  valor={clientes.morosos||0}     label="En mora"          color="#EF4444"/>
        <Card icon="⏸"  valor={clientes.suspendidos||0} label="Suspendidos"      color="#6366F1"/>
        <Card icon="📋" valor={clientes.total||0}       label="Total registrados" color="rgba(255,255,255,0.7)"/>
      </div>

      {/* Dos preguntas distintas, en dos filas distintas.
          Estaban juntas bajo un mismo título y parecía que debían sumar lo mismo: «cómo se
          conecta» y «de quién es el cliente» son cosas aparte, y sus totales no coinciden
          cuando hay clientes sin tipo de conexión. */}
      <SecTitle>Cómo se conectan · {clientes.con_servicio||0} con servicio</SecTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'12px', marginBottom:'28px' }}>
        <Card icon="🔵" valor={clientes.clientes_pppoe||0}  label="Fibra PPPoE"   color="#3B82F6"/>
        <Card icon="📡" valor={clientes.clientes_queue||0}  label="Radio (cola)"  color="#8B5CF6"/>
        {/* Sin usuario ni cola en el router no se les puede seguir el rastro: ni saber si
            están arriba, ni suspenderlos. Antes desaparecían en la diferencia. */}
        {/* Los de proveedor no llevan cuenta en nuestro router A PROPOSITO: ese servicio lo
            presta el proveedor. Tienen su casilla para que sumen con las demas sin parecer
            un problema. */}
        {(clientes.clientes_de_proveedor||0) > 0 && (
          <Card icon="🌐" valor={clientes.clientes_de_proveedor} label="Por un proveedor"
            color="#22D3EE" sub="no van en tu MikroTik"/>
        )}
        {(clientes.clientes_sin_tipo||0) > 0 && (
          <Card icon="⚠️" valor={clientes.clientes_sin_tipo} label="Sin conexión anotada"
            color="#FBBF24" ir="/clientes?filtro=sin_conexion"/>
        )}
      </div>

      <SecTitle>De quién son · {clientes.con_servicio||0} con servicio</SecTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:'12px', marginBottom:'28px' }}>
        <Card icon="👤" valor={clientes.clientes_directos||0}   label="Directos"        color="#10B981"/>
        <Card icon="🏢" valor={clientes.clientes_revendedor||0} label="Vía revendedor"  color="#F59E0B"/>
        {(clientes.clientes_sin_cobro||0) > 0 && (
          <Card icon="🎁" valor={clientes.clientes_sin_cobro} label="No se les cobra"
            color="#6EE7B7" ir="/clientes?filtro=no_se_cobra"/>
        )}
      </div>

      {revendedores.length > 0 && <>
        <SecTitle>Clientes por revendedor</SecTitle>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'12px', marginBottom:'28px' }}>
          {revendedores.map(r => <Card key={r.revendedor} icon="🏢" valor={r.cantidad} label={r.revendedor} color="#F59E0B"/>)}
        </div>
      </>}

      {proveedores.length > 0 && <>
        <SecTitle>Clientes por proveedor de red</SecTitle>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:'12px', marginBottom:'28px' }}>
          {proveedores.map(p => <Card key={p.proveedor} icon="🌐" valor={p.cantidad} label={p.proveedor} color="#06B6D4"/>)}
        </div>
      </>}

      <SecTitle>Infraestructura de red</SecTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:'12px', marginBottom:'28px' }}>
        <Card icon="📡" valor={infra.total||0}   label="Equipos (sin retirados)" color="#22C55E"/>
        <Card icon="✅" valor={infra.activos||0} label="En servicio"    color="#16A34A"/>
        {(infra.inactivos||0) > 0 && (
          <Card icon="⏸" valor={infra.inactivos} label="Fuera de servicio" color="#94A3B8"/>
        )}
        <Card icon="🔵" valor={infra.aps||0}    label="Access Points"  color="#3B82F6"/>
        <Card icon="📍" valor={infra.cpes||0}   label="CPEs"           color="#8B5CF6"/>
        <Card icon="↔️" valor={infra.ptps||0}   label="PTPs"           color="#F59E0B"/>
        {/* Antes esta tarjeta decía «CR». CR no es una clase de aparato sino que a ese
            cliente no se le cobra: lo que cuenta son las antenas en casa de los clientes. */}
        {(infra.antenas||0) > 0 && (
          <Card icon="📶" valor={infra.antenas} label="Antenas de cliente" color="#34D399"/>
        )}
        {/* Los equipos de fibra en casa del cliente. No tenían tarjeta: se sumaban al total
            y no aparecían en ningún desglose. */}
        {(infra.onus||0) > 0 && <Card icon="🟦" valor={infra.onus} label="ONU (fibra)" color="#22D3EE"/>}
        {(infra.rbs||0) > 0 && <Card icon="🔴" valor={infra.rbs} label="Routers/RB" color="#DC2626"/>}
        {(infra.naps||0) > 0 && <Card icon="🟢" valor={infra.naps} label="NAPs" color="#059669"/>}
      </div>

      <SecTitle>Cartera — {mesActual}</SecTitle>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'12px', marginBottom:'28px' }}>
        <Card icon="✅" valor={fmt(cartera.cobrado)}   label="Cobrado este mes"     color="#10B981"/>
        <Card icon="⏳" valor={fmt(cartera.pendiente)} label="Pendiente por cobrar" color="#F59E0B"/>
        <Card icon="🔴" valor={cartera.vencidos||0}    label="Cobros vencidos"      color="#EF4444"/>
      </div>

      {/* Estas dos cifras son solo la parte de LinkUs. A los clientes de un revendedor se les
          cobra la mensualidad entera, pero de cada una LinkUs se queda su tarifa y el resto es
          del revendedor. Sin esta linea el total se ve mas bajo sin motivo aparente. */}
      {Number(cartera.cobrado_de_revendedores) > 0 && (
        <div style={{ marginTop:'-18px', marginBottom:'28px', fontSize:'12.5px',
                      color:'rgba(255,255,255,0.6)', lineHeight:1.5 }}>
          Son solo lo de LinkUs. De lo recaudado este mes,{' '}
          <strong style={{ color:'#FDBA74' }}>{fmt(cartera.cobrado_de_revendedores)}</strong>{' '}
          entraron a la caja pero son de los revendedores.
        </div>
      )}
    </Layout>
  );
}
